import React, { useState, useEffect } from 'react';
import inventoryService from '../services/inventoryService';
import toast from 'react-hot-toast';
import { Plus, Trash2, X, Edit2, FolderTree, Search, CheckSquare, Square, ChevronRight, ChevronDown, GitBranch } from 'lucide-react';
import ConfirmModal from './ConfirmModal';

const InventoryCategoriesManager = () => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showTreeView, setShowTreeView] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [showBulkInactivateConfirm, setShowBulkInactivateConfirm] = useState(false);
  const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    parent_id: '',
    ativo: true
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await inventoryService.getCategories({ search });
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    loadCategories();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome) {
      toast.error('Nome da categoria é obrigatório');
      return;
    }

    try {
      const data = {
        nome: formData.nome,
        ativo: formData.ativo
      };

      if (formData.parent_id) {
        data.parent_id = formData.parent_id;
      } else {
        data.parent_id = null;
      }

      if (editingCategory) {
        // Atualizar categoria
        await inventoryService.updateCategory(editingCategory.id, data);
        toast.success('Categoria atualizada com sucesso!');
      } else {
        // Criar categoria
        await inventoryService.createCategory(data);
        toast.success('Categoria criada com sucesso!');
      }

      resetForm();
      setExpandedCategories([]);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar categoria');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      nome: category.nome || '',
      parent_id: category.parent_id || '',
      ativo: category.ativo
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setDeleteTargetId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await inventoryService.deleteCategory(deleteTargetId);
      toast.success('Categoria excluída com sucesso!');
      setSelectedCategories(prev => prev.filter(id => id !== deleteTargetId));
      setExpandedCategories([]);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao excluir categoria');
    } finally {
      setShowDeleteConfirm(false);
      setDeleteTargetId(null);
    }
  };

  const handleBulkActivate = async () => {
    if (selectedCategories.length === 0) {
      toast.error('Nenhuma categoria selecionada');
      return;
    }

    try {
      setLoading(true);
      let updated = 0;
      for (const id of selectedCategories) {
        await inventoryService.updateCategory(id, { ativo: true });
        updated++;
      }
      toast.success(`${updated} categoria(s) ativada(s) com sucesso!`);
      setSelectedCategories([]);
      setExpandedCategories([]);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao ativar categorias');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkInactivate = async () => {
    if (selectedCategories.length === 0) {
      toast.error('Nenhuma categoria selecionada');
      return;
    }
    setShowBulkInactivateConfirm(true);
  };

  const confirmBulkInactivate = async () => {
    try {
      setLoading(true);
      let updated = 0;
      for (const id of selectedCategories) {
        await inventoryService.updateCategory(id, { ativo: false });
        updated++;
      }
      toast.success(`${updated} categoria(s) inativada(s) com sucesso!`);
      setSelectedCategories([]);
      setExpandedCategories([]);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao inativar categorias');
    } finally {
      setLoading(false);
      setShowBulkInactivateConfirm(false);
    }
  };

  const clearSelection = () => {
    setSelectedCategories([]);
  };

  // Seleção individual
  const toggleSelectCategory = (id) => {
    setSelectedCategories(prev =>
      prev.includes(id)
        ? prev.filter(c => c !== id)
        : [...prev, id]
    );
  };

  // Seleção todos
  const toggleSelectAll = () => {
    if (selectedCategories.length === filteredCategories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(filteredCategories.map(c => c.id));
    }
  };

  // Excluir selecionados
  const handleDeleteSelected = async () => {
    if (selectedCategories.length === 0) {
      toast.error('Nenhuma categoria selecionada');
      return;
    }
    setShowDeleteSelectedConfirm(true);
  };

  const confirmDeleteSelected = async () => {
    try {
      setLoading(true);
      for (const id of selectedCategories) {
        await inventoryService.deleteCategory(id);
      }
      toast.success(`${selectedCategories.length} categoria(s) desativada(s) com sucesso!`);
      setSelectedCategories([]);
      setExpandedCategories([]);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao excluir categorias');
    } finally {
      setLoading(false);
      setShowDeleteSelectedConfirm(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingCategory(null);
    setFormData({
      nome: '',
      parent_id: '',
      ativo: true
    });
  };

  // Filtrar categorias pela busca
  const filteredCategories = categories.filter(cat =>
    cat.nome.toLowerCase().includes(search.toLowerCase())
  );

  // Filtrar categorias ativas para o select de parent_id
  const activeCategories = categories.filter(cat => cat.ativo);

  // Build tree structure
  const buildCategoryTree = (cats) => {
    const categoryMap = {};
    const roots = [];
    cats.forEach(cat => {
      categoryMap[cat.id] = { ...cat, children: [] };
    });
    cats.forEach(cat => {
      if (cat.parent_id && categoryMap[cat.parent_id]) {
        categoryMap[cat.parent_id].children.push(categoryMap[cat.id]);
      } else {
        roots.push(categoryMap[cat.id]);
      }
    });
    return roots;
  };

  // Count children recursively
  const countChildren = (category) => {
    let count = category.children?.length || 0;
    if (category.children) {
      category.children.forEach(child => {
        count += countChildren(child);
      });
    }
    return count;
  };

  // Toggle tree node expand
  const toggleExpand = (id) => {
    setExpandedCategories(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Expand all
  const expandAll = () => {
    const allIds = categories.map(c => c.id);
    setExpandedCategories(allIds);
  };

  // Collapse all
  const collapseAll = () => {
    setExpandedCategories([]);
  };

  const treeData = buildCategoryTree(filteredCategories);

  // Render tree node
  const renderTreeNode = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.includes(category.id);
    const directChildrenCount = category.children?.length || 0;
    const totalChildrenCount = countChildren(category);
    const isSelected = selectedCategories.includes(category.id);

    return (
      <React.Fragment key={category.id}>
        <tr
          className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''} ${!category.ativo ? 'bg-gray-100' : ''}`}
          style={{ marginLeft: level * 24 }}
        >
          <td className="px-4 py-4">
            <button
              onClick={() => toggleSelectCategory(category.id)}
              className="text-gray-500 hover:text-blue-600"
            >
              {isSelected ? (
                <CheckSquare className="w-5 h-5 text-blue-600" />
              ) : (
                <Square className="w-5 h-5" />
              )}
            </button>
          </td>
          <td className="px-6 py-4">
            <div className="flex items-center">
              {hasChildren ? (
                <button
                  onClick={() => toggleExpand(category.id)}
                  className="mr-2 text-gray-500 hover:text-gray-700 p-0.5"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
              ) : (
                <span className="w-5 mr-2" />
              )}
              <FolderTree className="w-5 h-5 text-gray-400 mr-3" />
              <div className="text-sm font-medium text-gray-900">{category.nome}</div>
              {directChildrenCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                  {directChildrenCount} {directChildrenCount === 1 ? 'filha' : 'filhas'}
                </span>
              )}
              {category.product_count > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                  {category.product_count} {category.product_count === 1 ? 'produto' : 'produtos'}
                </span>
              )}
            </div>
          </td>
          <td className="px-6 py-4 text-sm text-gray-500">
            {category.parent_id
              ? categories.find(c => c.id === category.parent_id)?.nome || '-'
              : '-'}
          </td>
          <td className="px-6 py-4">
            <span className={`px-2 py-1 text-xs rounded-full ${
              category.ativo
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-200 text-gray-600'
            }`}>
              {category.ativo ? 'Ativo' : 'Inativo'}
            </span>
          </td>
          <td className="px-6 py-4 text-right">
            <div className="flex justify-end gap-2">
              <button
                onClick={() => handleEdit(category)}
                className="text-blue-600 hover:text-blue-800 p-1"
                title="Editar"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(category.id)}
                className="text-red-600 hover:text-red-800 p-1"
                title="Excluir"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </td>
        </tr>
        {isExpanded && hasChildren && category.children.map(child => renderTreeNode(child, level + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categorias de Produtos</h1>
          <p className="text-gray-600">Gerencie as categorias do catálogo de produtos</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Nova Categoria
        </button>
      </div>

      {/* Search and Actions */}
      <div className="mb-6 flex gap-4 flex-wrap">
        <form onSubmit={handleSearch} className="flex-1 flex gap-4 min-w-[300px]">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar categorias..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Buscar
          </button>
        </form>

        {selectedCategories.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-sm text-blue-700 font-medium">
              {selectedCategories.length} selecionado(s)
            </span>
            <div className="flex gap-2">
              <button
                onClick={handleBulkActivate}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
              >
                Ativar
              </button>
              <button
                onClick={handleBulkInactivate}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
              >
                Inativar
              </button>
              <button
                onClick={clearSelection}
                className="flex items-center gap-1 px-3 py-1.5 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
        <button
          onClick={() => setShowTreeView(!showTreeView)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            showTreeView
              ? 'bg-purple-600 text-white hover:bg-purple-700'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
          title={showTreeView ? 'Visão em lista' : 'Visão em árvore'}
        >
          <GitBranch className="w-4 h-4" />
          {showTreeView ? 'Lista' : 'Árvore'}
        </button>
      </div>

      {/* Categories List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {showTreeView && (
          <div className="px-4 py-3 bg-purple-50 border-b border-purple-100 flex items-center justify-between">
            <span className="text-sm text-purple-700 font-medium flex items-center gap-2">
              <GitBranch className="w-4 h-4" />
              Visão Hierárquica
            </span>
            <div className="flex gap-2">
              <button
                onClick={expandAll}
                className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
              >
                Expandir todos
              </button>
              <button
                onClick={collapseAll}
                className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
              >
                Recolher todos
              </button>
            </div>
          </div>
        )}
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={toggleSelectAll}
                  className="text-gray-500 hover:text-gray-700"
                  title={selectedCategories.length === filteredCategories.length ? "Desmarcar todos" : "Selecionar todos"}
                >
                  {selectedCategories.length === filteredCategories.length && filteredCategories.length > 0 ? (
                    <CheckSquare className="w-5 h-5" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nome
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Categoria Pai
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  Carregando...
                </td>
              </tr>
            ) : filteredCategories.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  Nenhuma categoria encontrada
                </td>
              </tr>
            ) : showTreeView ? (
              treeData.map(category => renderTreeNode(category))
            ) : (
              filteredCategories.map((category) => (
                <tr key={category.id} className={`hover:bg-gray-50 ${selectedCategories.includes(category.id) ? 'bg-blue-50' : ''} ${!category.ativo ? 'bg-gray-100' : ''}`}>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => toggleSelectCategory(category.id)}
                      className="text-gray-500 hover:text-blue-600"
                    >
                      {selectedCategories.includes(category.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <FolderTree className="w-5 h-5 text-gray-400 mr-3" />
                      <div className="text-sm font-medium text-gray-900">{category.nome}</div>
                      {category.product_count > 0 && (
                        <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                          {category.product_count} {category.product_count === 1 ? 'produto' : 'produtos'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {category.parent_id
                      ? categories.find(c => c.id === category.parent_id)?.nome || '-'
                      : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      category.ativo
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {category.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleEdit(category)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
              </h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Categoria *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Puxadores"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria Pai (opcional)
                </label>
                <select
                  value={formData.parent_id}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Nenhuma (categoria raiz)</option>
                  {activeCategories
                    .filter(c => c.id !== editingCategory?.id)
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.nome}</option>
                    ))}
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="ativo" className="ml-2 text-sm text-gray-700">
                  Categoria ativa
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingCategory ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        show={showDeleteConfirm}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeleteTargetId(null);
        }}
        title="Desativar Categoria"
        message="Desativar esta categoria? A categoria ficará inativa e não poderá ser usada em novos produtos."
        confirmText="Desativar"
        cancelText="Cancelar"
        type="danger"
      />

      <ConfirmModal
        show={showBulkInactivateConfirm}
        onConfirm={confirmBulkInactivate}
        onCancel={() => setShowBulkInactivateConfirm(false)}
        title="Inativar Categorias"
        message={`Inativar ${selectedCategories.length} categoria(s)? As categorias ficarão inativas e não poderão ser usadas em novos produtos.`}
        confirmText="Inativar"
        cancelText="Cancelar"
        type="warning"
      />

      <ConfirmModal
        show={showDeleteSelectedConfirm}
        onConfirm={confirmDeleteSelected}
        onCancel={() => setShowDeleteSelectedConfirm(false)}
        title="Desativar Categorias"
        message={`Desativar ${selectedCategories.length} categoria(s)? As categorias ficarão inativas e não poderão ser usadas em novos produtos.`}
        confirmText="Desativar"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default InventoryCategoriesManager;
